import React from "react"

import { graphql } from "gatsby"
import PropTypes from "prop-types"

import CopyContainer from "../components/atoms/copy-container"
import Wrapper from "../components/atoms/wrapper"
import Seo from "../components/head"
import Layout from "../components/layout"
import Accordion from "../components/molecules/accordion"
import ButtonGroup from "../components/molecules/button-group"
import Columns from "../components/molecules/columns"
import Hero from "../components/molecules/hero"
import Testimonials from "../components/molecules/testimonials"
import Collection from "../components/organisms/collection"
import Sidebar from "../components/organisms/sidebar"

const isRegion = (region) =>
  !region || region.abbreviation === process.env.GATSBY_COUNTRY

const presentingPartner = (data) =>
  data.sanityPage.presenting_partners?.find((b) => isRegion(b.country))

const PageTemplate = ({ data }) => (
  <Layout
    customHeader={{
      ...data.sanityPage.header,
      ...presentingPartner(data),
    }}
    color={data.sanityPage.color}
  >
    {data.sanityPage.content.map((content) => {
      if (content._type === "page_rich_content" && isRegion(content.country)) {
        return (
          <div id={`section_${content._key}`}>
            <Wrapper container spacer>
              <CopyContainer
                content={content._rawContent}
                alignment={content.alignment}
                customClass={content.custom_class}
                bg={data.sanityPage.color?.replaceAll("-", "")}
                full
              />
            </Wrapper>
          </div>
        )
      }
      if (content._type === "page_ctas" && isRegion(content.country)) {
        return (
          <div id={`section_${content._key}`}>
            <Wrapper container spacer>
              <ButtonGroup
                ctas={content.ctas}
                large={content.large}
                alignment="centered"
                gatingVertical={
                  content.gating ? content.vertical?.slug.current : null
                }
                gatingTitle={content.gating_title}
                gatingCopy={content._rawGatingCopy}
                gatingButtonText={content.gating_button_text}
                external
              />
            </Wrapper>
          </div>
        )
      }
      if (content._type === "page_quotes" && isRegion(content.country)) {
        return (
          <div id={`section_${content._key}`}>
            <Testimonials
              title={content.title}
              color={content.color}
              quotes={content.quotes}
            />
          </div>
        )
      }
      if (content._type === "page_hero" && isRegion(content.country)) {
        return (
          <div id={`section_${content._key}`}>
            <Hero
              title={content.title}
              description={content._rawContent}
              image={content.image}
              color={content.color?.replaceAll("-", "")}
              colorHex={
                content.partner?.color?.hex ||
                presentingPartner(data)?.partner?.color?.hex
              }
              alignment={content.alignment}
              border={content.border}
              partner={content.partner}
              aspectRatio={content.aspect_ratio}
              ctas={content.ctas}
              full={content.full}
              video={content.video}
              illustration={content.illustration?.image}
              styleVariant={content.style_variant}
              bgColor={data.sanityPage.color || undefined}
            />
          </div>
        )
      }
      if (content._type === "page_columns" && isRegion(content.country)) {
        return (
          <div id={`section_${content._key}`}>
            <Wrapper container spacer>
              <Columns data={content.columns} />
            </Wrapper>
          </div>
        )
      }
      if (content._type === "page_accordion" && isRegion(content.country)) {
        return (
          <div id={`section_${content._key}`}>
            <Wrapper container spacer>
              <Accordion data={content.items} />
            </Wrapper>
          </div>
        )
      }
      if (content._type === "page_collection" && isRegion(content.country)) {
        return (
          <div id={`section_${content._key}`}>
            <Wrapper container spacer>
              <Collection
                title={content.title}
                description={content._rawDescription}
                color={content.color}
                feature={content.feature}
                collection={
                  content.custom ? content.custom_items : content.resources
                }
              />
            </Wrapper>
          </div>
        )
      }
      if (content._type === "page_sidebar" && isRegion(content.country)) {
        return (
          <div id={`section_${content._key}`}>
            <Sidebar
              sidebar={
                content.custom ? content.custom_items : content.resources
              }
              title={content.title}
              secondaryTitle={content.secondary_title}
              customTitle
              color={content.color}
            />
          </div>
        )
      }
      return null
    })}
  </Layout>
)

PageTemplate.propTypes = {
  data: PropTypes.object.isRequired,
}

export default PageTemplate

const HeadComponent = ({ data, location }) => (
  <Seo
    title={data.sanityPage.title}
    description={data.sanityPage.description}
    isAd={!presentingPartner(data)}
    image={
      data.sanityPage.image
        ? `${data.site.siteMetadata.siteUrl}${data.sanityPage.image.asset.publicUrl}`
        : null
    }
    path={location.pathname}
  />
)

HeadComponent.propTypes = {
  data: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
}

export const Head = HeadComponent

export const pageQuery = graphql`
  query pageQuery($id: String!) {
    site {
      siteMetadata {
        siteUrl
      }
    }
    sanityPage(id: { eq: $id }) {
      title
      image {
        ...ImageWithPreview
        asset {
          publicUrl
        }
      }
      description
      color
      header {
        ctas {
          country {
            abbreviation
          }
          url
          text
          variant
        }
        navigation {
          items {
            title
            type
            url
            submenu {
              items {
                url
                title
                type
                email_subject
                email_body
                email_address
              }
            }
            email_body
            email_address
            email_subject
          }
        }
      }
      presenting_partners {
        country {
          abbreviation
        }
        url
        partner {
          name
          logo {
            ...ImageWithPreview
          }
          url
          color {
            hex
          }
        }
      }
      content {
        ... on SanityPageColumns {
          _type
          _key
          country {
            abbreviation
          }
          columns {
            color
            border
            ctas {
              text
              url
              variant
            }
            custom_class
            alignment
            _rawContent
          }
        }
        ... on SanityPageCtas {
          _type
          _key
          country {
            abbreviation
          }
          large
          gating
          gating_title
          gating_button_text
          _rawGatingCopy
          vertical {
            slug {
              current
            }
          }
          ctas {
            text
            url
            variant
          }
        }
        ... on SanityPageAccordion {
          _type
          _key
          country {
            abbreviation
          }
          items {
            title
            _rawContent
          }
        }
        ... on SanityPageQuotes {
          _type
          _key
          country {
            abbreviation
          }
          color
          title
          quotes {
            quote
            attribution
          }
        }
        ... on SanityPageCollection {
          _type
          _key
          country {
            abbreviation
          }
          title
          _rawDescription
          feature
          color
          custom
          custom_items {
            title
            label
            url
            date
            abstract
            image {
              ...ImageWithPreview
            }
          }
          resources {
            ... on SanityPage {
              title
              image {
                ...ImageWithPreview
              }
              abstract: description
              type: _type
              slug {
                current
              }
              presenting_partners {
                country {
                  abbreviation
                }
                partner {
                  prefix
                  name
                  logo {
                    ...ImageWithPreview
                  }
                  dark_logo {
                    ...ImageWithPreview
                  }
                }
              }
            }
            ... on SanityPickem {
              title
              date: start_date
              abstract: description
              image {
                ...ImageWithPreview
              }
              type: _type
              slug {
                current
              }
              presenting_partners {
                country {
                  abbreviation
                }
                partner {
                  prefix
                  name
                  logo {
                    ...ImageWithPreview
                  }
                  dark_logo {
                    ...ImageWithPreview
                  }
                }
              }
            }
            ... on SanityBracket {
              title
              image {
                ...ImageWithPreview
              }
              abstract: description
              type: _type
              slug {
                current
              }
              date: start_date
              presenting_partners {
                country {
                  abbreviation
                }
                partner {
                  prefix
                  name
                  logo {
                    ...ImageWithPreview
                  }
                  dark_logo {
                    ...ImageWithPreview
                  }
                }
              }
            }
            ... on SanityPodcast {
              type: _type
              episode_number
              date
              title
              _rawAbstract: _rawSummary
              slug {
                current
              }
              image {
                ...ImageWithPreview
              }
            }
            ... on SanityContest {
              type: _type
              image {
                ...ImageWithPreview
              }
              title: name
              abstract
              date: end_date
              slug {
                current
              }
            }
            ... on SanityQuiz {
              id
              title
              type: _type
              slug {
                current
              }
              partner {
                prefix
                name
                logo {
                  ...ImageWithPreview
                }
                dark_logo {
                  ...ImageWithPreview
                }
              }
              partner_url
              abstract
              image {
                ...ImageWithPreview
              }
            }
            ... on SanityFaqGlossary {
              id
              faqGlossarySport: sport {
                name
                emoji
                slug {
                  current
                }
              }
              abstract
              image: featured_image {
                ...ImageWithPreview
              }
              type
              _type
            }
            ... on SanityArticle {
              id
              country {
                abbreviation
              }
              abstract
              date
              detailed_title
              title
              featured_image {
                image {
                  ...ImageWithPreview
                }
              }
              sport {
                slug {
                  current
                }
                emoji
                color
                name
              }
              slug {
                current
              }
              partner {
                prefix
                name
                logo {
                  ...ImageWithPreview
                }
                dark_logo {
                  ...ImageWithPreview
                }
              }
              web_article_type
              vertical {
                color
                name
                slug {
                  current
                }
              }
            }
          }
        }
        ... on SanityPageSidebar {
          _key
          _type
          country {
            abbreviation
          }
          title
          secondary_title
          color
          custom
          custom_items {
            title
            label
            url
            image {
              ...ImageWithPreview
            }
          }
          resources {
            ... on SanityPage {
              title
              image {
                ...ImageWithPreview
              }
              type: _type
              slug {
                current
              }
            }
            ... on SanityBracket {
              title
              image {
                ...ImageWithPreview
              }
              type: _type
              slug {
                current
              }
            }
            ... on SanityBracket {
              title
              image {
                ...ImageWithPreview
              }
              type: _type
              slug {
                current
              }
            }
            ... on SanityPickem {
              title
              image {
                ...ImageWithPreview
              }
              type: _type
              slug {
                current
              }
            }
            ... on SanityPodcast {
              type: _type
              title
              episode_number
              slug {
                current
              }
              image {
                ...ImageWithPreview
              }
            }
            ... on SanityContest {
              type: _type
              image {
                ...ImageWithPreview
              }
              title: name
              slug {
                current
              }
            }
            ... on SanityQuiz {
              id
              title
              type: _type
              slug {
                current
              }
              image {
                ...ImageWithPreview
              }
            }
            ... on SanityFaqGlossary {
              id
              faqGlossarySport: sport {
                name
                slug {
                  current
                }
              }
              image: featured_image {
                ...ImageWithPreview
              }
              type
              _type
            }
            ... on SanityArticle {
              id
              detailed_title
              title
              country {
                abbreviation
              }
              featured_image {
                image {
                  ...ImageWithPreview
                }
              }
              slug {
                current
              }
              web_article_type
            }
          }
        }
        ... on SanityPageHero {
          _type
          _key
          aspect_ratio
          country {
            abbreviation
          }
          title
          full
          illustration {
            image {
              ...ImageWithPreview
            }
          }
          style_variant
          partner {
            name
            prefix
            color {
              hex
            }
            logo {
              ...ImageWithPreview
            }
          }
          alignment
          border
          color
          video
          image {
            ...ImageWithPreview
          }
          ctas {
            url
            text
            variant
          }
          _rawContent
        }
        ... on SanityPageRichContent {
          _type
          _key
          custom_class
          country {
            abbreviation
          }
          alignment
          _rawContent
        }
      }
    }
  }
`
